import React, {useState, useEffect, useCallback} from 'react';
import {Modal, Text, TextInput, Button, Icon, StatusBadge} from '@/ui-kits';
import {SupportTicket} from '@/interface';
import {sendReplyRequest, markAsSpamRequest, resetSupportAdminMessage} from '@/services/redux/slices';
import {useDispatch, useSelector} from 'react-redux';
import {
  supportAdminSuccessSelector,
  supportAdminFailureSelector,
  isSendingReplySelector,
  isMarkingAsSpamSelector,
} from '@/services/redux/selectors';
import {ErrorCodes, getErrorMessage, getSuccessMessage, SuccessCodes} from '@/utils/getMessages';
import {useToast} from '@/hooks';
import { HelpRequestStatusBadgeVariants, HelpRequestStatusLabels } from '@/constants';
import { HelpRequestStatus } from '@/constants/enums';

interface OpenStatusModalProps {
  open: boolean;
  onClose: () => void;
  ticket: SupportTicket;
  onSuccess: () => void;
}

export const OpenStatusModal: React.FC<OpenStatusModalProps> = ({
  open,
  onClose,
  ticket,
  onSuccess,
}) => {

// ==========================
// Hooks 
// ==========================
  const dispatch = useDispatch();
  const {showToast} = useToast();

// ==========================
// Redux Selectors
// ==========================
  const success = useSelector(supportAdminSuccessSelector) as SuccessCodes;
  const failure = useSelector(supportAdminFailureSelector) as ErrorCodes;
  const isSendingReply = useSelector(isSendingReplySelector);
  const isMarkingAsSpam = useSelector(isMarkingAsSpamSelector);

// ==========================
// Local State
// ==========================
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');

// ==========================
// Effects
// ==========================

   /**
    * Reset form fields when modal opens
   */
  useEffect(() => {
    if (open) {
      setSubject('');
      setMessage('');
    }
  }, [open]);

  /**
   * Handle success and failure messages
   */
  useEffect(() => {
    if (success && (success === 'S-10092' || success === 'S-10093')) {
      showToast(getSuccessMessage(success as SuccessCodes), 'success');
      onSuccess();
    }
    if (failure && failure !== 'E-10044') {
      showToast(getErrorMessage(failure), 'failure');
    }
    return () => {
      dispatch(resetSupportAdminMessage());
    };
  }, [success, failure, onSuccess, showToast, dispatch]);

  // ==========================
  // Event Handlers
  // ==========================
  const handleSendReply = useCallback(() => {
    if (!subject.trim() || !message.trim()) {
      showToast('Subject and message cannot be empty.', 'failure');
      return;
    }
    dispatch(
      sendReplyRequest({
        id: ticket.id,
        subject: subject.trim(),
        message: message.trim(),
      }),
    );
  }, [dispatch, ticket.id, subject, message, showToast]);


  const handleMarkAsSpam = useCallback(() => {
    dispatch(markAsSpamRequest({id: ticket.id}));
  }, [dispatch, ticket.id]);

  const isFormValid = subject.trim().length > 0 && message.trim().length > 0;

  return (
    <Modal
      open={open}
      onClose={onClose}
      className="relative bg-white rounded-xl w-[95vw] sm:w-[90vw] max-w-[700px] max-h-[95vh] sm:max-h-[90vh] flex flex-col">
      {/* Close Button */}
      <button
        onClick={onClose}
        className="absolute top-2 right-2 sm:top-4 sm:right-4 size-7 sm:size-8 flex items-center justify-center rounded-full hover:bg-gray-100 z-10">
        <Icon type="close" className="size-4 sm:size-5 text-ui_blue" />
      </button>

      {/* Fixed Header Section */}
      <div className="flex items-center gap-2 sm:gap-4 px-4 sm:px-6 pt-3 sm:pt-4 pb-3 sm:pb-4 flex-shrink-0">
        <Text variant="free" className="text-ui_blue font-FigtreeBold text-xl sm:text-2xl md:text-3xl">
          {String(ticket.id)}
        </Text>
        <StatusBadge variant="secondary" status={HelpRequestStatusBadgeVariants[ticket?.status as HelpRequestStatus]}>
          {HelpRequestStatusLabels[ticket?.status as HelpRequestStatus]}
        </StatusBadge>
      </div>

      {/* Scrollable Content Section */}
      <div className="flex-1 overflow-y-auto px-4 sm:px-6 pt-0 pb-3 sm:pb-4">
        {/* User Message Section */}
        <div className="mb-4 sm:mb-6">
          <Text variant="body" className="!text-deep_blue !font-FigtreeSemiBold mb-2 sm:mb-3 !text-base sm:!text-lg md:!text-xl">
            User Message
          </Text>
          <div className="bg-ui_user_message_bg/5 rounded-lg p-2 sm:p-3 border border-ui_input_border">
            <Text variant="body" className="text-ui_blue whitespace-pre-wrap text-sm sm:text-base">
              {ticket.user_message || 'No message provided'}
            </Text>
          </div>
        </div>

        {/* Reply to User Section */}
        <div className="mb-0 bg-ui_reply_section rounded-lg p-3 sm:p-4 border border-ui_input_border">
          <Text variant="body" className="!text-deep_blue !font-FigtreeSemiBold mb-2 sm:mb-3 !text-lg sm:!text-xl md:!text-2xl">
            Reply to User
          </Text>
          <div className="space-y-3 sm:space-y-4">
            {/* Subject Input */}
            <TextInput
              label="Subject"
              value={subject}
              onChangeText={setSubject}
              placeholder="Please write a subject..."
              className="w-full"
              labelClassName="!text-deep_blue"
              wrapperClassName="!border-ui_placeholder bg-white"
            />
            {/* Message Input */}
            <TextInput
              label="Message"
              value={message}
              onChangeText={setMessage}
              placeholder="Type your reply here..."
              className="w-full"
              labelClassName="!text-deep_blue"
              wrapperClassName="!border-ui_placeholder bg-white"
              multiline
            />
          </div>
        </div>
      </div>

      {/* Fixed Action Buttons */}
      <div className="flex flex-row gap-2 sm:gap-4 px-4 sm:px-6 pt-3 sm:pt-4 pb-3 sm:pb-4 flex-shrink-0">
        <Button
          variant="secondary"
          title="Mark as Spam"
          onClick={handleMarkAsSpam}
          disabled={isMarkingAsSpam}
          isLoading={isMarkingAsSpam}
          className="flex-1 h-9 sm:h-auto"
        />
        <Button
          variant="primary"
          title="Send Reply"
          onClick={handleSendReply}
          disabled={!isFormValid || isSendingReply}
          isLoading={isSendingReply}
          className="flex-1 h-9 sm:h-auto"
          textClassName="!whitespace-nowrap"
          leftIcon="send_reply"
        />
      </div>
    </Modal>
  );
};

